import { LegalPageLayout, P, UL, LI, H3, Strong, ContactLink } from './LegalPageLayout';

/**
 * Privacy Policy. APPI (個人情報保護法) is the lead framework; GDPR (EU/UK)
 * and CCPA/CPRA (California) are handled as first-class addenda. PlyEZ acts
 * as controller for account, billing, and marketing data, and as processor
 * for Customer Data flowing through the Service (governed by the DPA at
 * /legal/dpa). Sub-processors are enumerated at /legal/subprocessors.
 */
export function PrivacyPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.privacy"
      lastUpdated="2026-06-03"
      lead={
        <>
          This Privacy Policy explains what personal information PlyEZ
          ([registered company name], a Japanese 株式会社 — &ldquo;PlyEZ,&rdquo;
          &ldquo;the Company,&rdquo; &ldquo;we,&rdquo; or &ldquo;us&rdquo;)
          collects through plyez.com and the PlyEZ application, why we collect
          it, who we share it with, and the rights you have over it. Japan&rsquo;s
          Act on the Protection of Personal Information (個人情報保護法, the
          &ldquo;APPI&rdquo;) is our lead framework; dedicated sections below
          address the EU/UK General Data Protection Regulation (GDPR) and the
          California Consumer Privacy Act as amended by the CPRA (CCPA/CPRA).
        </>
      }
      sections={[
        {
          id: 'roles',
          title: '1. Our role: controller and processor',
          body: (
            <>
              <P>
                PlyEZ plays two distinct roles depending on the data in question:
              </P>
              <UL>
                <LI>
                  <Strong>Controller (個人情報取扱事業者 as the entity that
                  determines purpose and means)</Strong> — for marketing-site
                  analytics, account sign-up data, billing and tax records, and
                  support correspondence. This Privacy Policy governs that
                  processing.
                </LI>
                <LI>
                  <Strong>Processor</Strong> — for Customer Data that our customers
                  push through the Service (invoices, vendor master records,
                  expense receipts, payment instructions, AI prompts, and the like).
                  We process that data on documented instructions under our{' '}
                  <ContactLink to="/legal/dpa">Data Processing Agreement</ContactLink>,
                  which controls in the event of any conflict with this Policy.
                </LI>
              </UL>
              <P>
                Where this Policy refers to &ldquo;personal information&rdquo; it
                includes &ldquo;personal data&rdquo; under the GDPR and
                &ldquo;personal information&rdquo; under the CCPA/CPRA, except where
                a specific framework is named.
              </P>
            </>
          ),
        },
        {
          id: 'what',
          title: '2. What we collect',
          body: (
            <>
              <H3>Account and identity data</H3>
              <UL>
                <LI>Name, work email, profile photo, and language/locale preference;</LI>
                <LI>OAuth subject identifiers when you sign in with Google or Apple;</LI>
                <LI>A salted password hash (bcrypt) — never the plaintext password;</LI>
                <LI>Role, workspace membership, and access-control assignments.</LI>
              </UL>

              <H3>Company and entity data</H3>
              <UL>
                <LI>Legal entity name, registered address, and Japan Corporate Number (法人番号);</LI>
                <LI>Hanko seal text, registered representative, and entity metadata;</LI>
                <LI>Tax-registration identifiers used for invoicing and compliance.</LI>
              </UL>

              <H3>Invoice, vendor, and payables data</H3>
              <UL>
                <LI>Invoices, bills, receipts, and the structured fields extracted from them;</LI>
                <LI>
                  Vendor and counterparty master records, including taxpayer
                  identification numbers (TINs / 法人番号 / 適格請求書発行事業者登録番号)
                  and banking details used to make payments;
                </LI>
                <LI>Ledger entries, matching results, approvals, and payment instructions.</LI>
              </UL>
              <P>
                Vendor TINs and banking details are treated as sensitive and are
                encrypted at rest with AES-256-GCM (see Section 8).
              </P>

              <H3>Usage, device, and log data</H3>
              <UL>
                <LI>IP address, browser type, and referrer (via Cloudflare edge logs);</LI>
                <LI>Approximate country/city derived from IP for locale and currency defaults;</LI>
                <LI>Feature usage and tamper-evident audit-log entries (who did what, when, from which IP);</LI>
                <LI>Form submissions on /contact, /demo, and enterprise inquiries.</LI>
              </UL>

              <H3>Cookies and local storage</H3>
              <P>
                We use a minimal set of cookies and browser local storage for
                authentication, language, and theme preferences. We do not use
                advertising or cross-site tracking cookies. The full inventory is in
                our <ContactLink to="/legal/cookies">Cookie Policy</ContactLink>.
              </P>
              <P>
                We <Strong>do not</Strong> sell or share personal information for
                cross-context behavioral advertising, and we <Strong>do not</Strong>{' '}
                use Customer Data to train AI models.
              </P>
            </>
          ),
        },
        {
          id: 'use',
          title: '3. Purposes and legal bases',
          body: (
            <>
              <P>
                Under the APPI we specify the purposes of use (利用目的) for which we
                handle personal information. Under the GDPR we also identify a legal
                basis for each purpose. The table below applies both.
              </P>
              <UL>
                <LI>
                  <Strong>Provide and operate the Service</Strong> — authenticate
                  users, enforce role-based access, run AP/AR, expense, payments,
                  ledger, and JP-compliance workflows.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: performance of a contract (Art. 6(1)(b)).
                  </em>
                </LI>
                <LI>
                  <Strong>Transactional communications</Strong> — verification,
                  password reset, billing receipts, and security alerts.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: contract / legal obligation.
                  </em>
                </LI>
                <LI>
                  <Strong>Security, fraud, and abuse prevention</Strong> — detect,
                  investigate, and prevent incidents, and maintain the audit log.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: legitimate interests (Art. 6(1)(f)).
                  </em>
                </LI>
                <LI>
                  <Strong>Legal and tax compliance</Strong> — retain books and
                  records under the Electronic Books Preservation Act (電子帳簿保存法)
                  and respond to lawful requests.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: legal obligation (Art. 6(1)(c)).
                  </em>
                </LI>
                <LI>
                  <Strong>Service improvement and support</Strong> — diagnose issues,
                  improve reliability, and respond to inquiries.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: legitimate interests.
                  </em>
                </LI>
                <LI>
                  <Strong>Marketing</Strong> — product news and updates, sent only
                  with separate opt-in and unsubscribable from any email footer.{' '}
                  <em className="text-muted-foreground not-italic">
                    GDPR basis: consent (Art. 6(1)(a)).
                  </em>
                </LI>
              </UL>
              <P>
                Where processing relies on legitimate interests, we balance those
                interests against your rights and freedoms and will provide details
                of that balancing test on request.
              </P>
            </>
          ),
        },
        {
          id: 'ai',
          title: '4. AI processing',
          body: (
            <>
              <P>
                PlyEZ is AI-native. To extract fields from documents, match invoices,
                and power semantic search, we send the relevant document text and
                structured metadata to third-party AI infrastructure:
              </P>
              <UL>
                <LI>
                  <Strong>OpenRouter</Strong> acts as our AI inference gateway,
                  routing requests to upstream model providers. Document content and
                  the metadata needed for the task are transmitted to OpenRouter and,
                  through it, to the selected model provider.
                </LI>
                <LI>
                  <Strong>Qdrant</Strong> stores vector embeddings derived from your
                  documents to power semantic search and matching.
                </LI>
              </UL>
              <P>
                We contract for <Strong>zero-retention / no-training</Strong> handling
                of inference inputs and outputs where the upstream provider supports
                it, and Customer Data is never used to train PlyEZ&rsquo;s or any
                provider&rsquo;s models. The current upstream provider list is
                available on request and through the DPA addendum. Enterprise
                customers can pin AI routing to a single approved region or model.
                AI extraction is a decision-support aid; meaningful human review
                remains available before financially significant actions, and you can
                request human review of any automated output by contacting{' '}
                <a className="text-accent-700 underline" href="mailto:support@plyez.com">support@plyez.com</a>.
              </P>
            </>
          ),
        },
        {
          id: 'sharing',
          title: '5. Sub-processors and disclosures',
          body: (
            <>
              <P>
                We share personal information with a vetted set of sub-processors that
                help us deliver the Service. The complete, current list — with each
                provider&rsquo;s purpose, data category, and region — is maintained at{' '}
                <ContactLink to="/legal/subprocessors">/legal/subprocessors</ContactLink>.
                In summary, our core sub-processors include:
              </P>
              <UL>
                <LI><Strong>Hetzner</Strong> — hosting and compute (Germany/EU);</LI>
                <LI><Strong>Cloudflare R2</Strong> — encrypted object storage (EU-jurisdiction option available);</LI>
                <LI><Strong>Cloudflare</Strong> — CDN, DDoS protection, and bot management;</LI>
                <LI><Strong>OpenRouter</Strong> — AI inference gateway routing to model providers;</LI>
                <LI><Strong>Qdrant</Strong> — vector search and semantic matching;</LI>
                <LI><Strong>Stripe</Strong> — billing and card issuing;</LI>
                <LI><Strong>Wise</Strong> — cross-border payouts;</LI>
                <LI><Strong>Amazon SES</Strong> — transactional email delivery.</LI>
              </UL>
              <P>
                When enabled for your workspace, we may also use{' '}
                <Strong>GMO Payment Gateway</Strong> (JP card issuance), a{' '}
                <Strong>Peppol AS4 access-point partner</Strong> (e.g.
                Storecove/Pagero) for e-invoicing, and an accredited{' '}
                <Strong>RFC 3161 time-stamping authority</Strong> (e.g.
                セコム/三菱電機) for trusted timestamps.
              </P>
              <P>
                Under the APPI, sharing with sub-processors that handle data on our
                instructions is treated as entrustment of handling (委託); we supervise
                them under written agreements rather than as third-party provision
                (第三者提供). We also disclose information where required to comply with
                lawful process (e.g., court orders or requests from competent
                authorities). We push back on overbroad requests and will notify
                affected customers where we are not legally prohibited from doing so.
              </P>
            </>
          ),
        },
        {
          id: 'transfers',
          title: '6. International transfers',
          body: (
            <>
              <P>
                PlyEZ is established in Japan, and our primary infrastructure runs on
                Hetzner in Germany (EU). Personal information may also be processed in
                other regions by our sub-processors — for example, Cloudflare&rsquo;s
                global edge, Stripe (United States and Ireland), Wise (UK/EEA), and AI
                model providers reached through OpenRouter.
              </P>
              <UL>
                <LI>
                  <Strong>APPI cross-border provision (越境移転)</Strong> — where the
                  APPI applies, we provide the information required for cross-border
                  transfers and rely on your consent or on equivalent safeguards with
                  the recipient, and we inform you of the relevant measures on request.
                </LI>
                <LI>
                  <Strong>GDPR transfers</Strong> — for transfers of EU/UK personal
                  data to countries without an adequacy decision, we rely on the
                  European Commission&rsquo;s <Strong>Standard Contractual Clauses</Strong>{' '}
                  (and the UK International Data Transfer Addendum) together with
                  supplementary technical and organizational measures.
                </LI>
              </UL>
              <P>
                Enterprise customers can pin storage and processing to an
                EU-jurisdiction configuration and pin AI calls to a single approved
                provider/region. Contact{' '}
                <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
                for transfer documentation.
              </P>
            </>
          ),
        },
        {
          id: 'retention',
          title: '7. Retention',
          body: (
            <>
              <P>
                We retain personal information only as long as necessary for the
                purposes set out above, then delete or anonymize it.
              </P>
              <UL>
                <LI>
                  <Strong>Account data</Strong> — for the life of your account plus a
                  30-day grace period after cancellation to allow export.
                </LI>
                <LI>
                  <Strong>Invoices, ledger, and tax records</Strong> — retained for{' '}
                  <Strong>7 years</Strong> to comply with Japan&rsquo;s Electronic
                  Books Preservation Act (電子帳簿保存法) and related record-keeping
                  obligations.
                </LI>
                <LI>
                  <Strong>Audit-log entries</Strong> — retained for 7 years as
                  tamper-evident records of system activity.
                </LI>
                <LI>
                  <Strong>Marketing-list data</Strong> — until you unsubscribe, after
                  which we remove your email within 30 days.
                </LI>
              </UL>
              <P>
                Statutory retention obligations may require us to keep certain records
                even after you ask us to delete them; in that case we restrict
                processing to what the obligation requires.
              </P>
            </>
          ),
        },
        {
          id: 'security',
          title: '8. Security',
          body: (
            <>
              <P>
                We protect personal information with technical and organizational
                measures appropriate to its sensitivity:
              </P>
              <UL>
                <LI><Strong>Encryption at rest</Strong> — AES-256-GCM for sensitive fields such as vendor TINs and banking details;</LI>
                <LI><Strong>Encryption in transit</Strong> — TLS 1.3;</LI>
                <LI><Strong>Tamper-evident audit log</Strong> — system activity is recorded in a log that detects backdated or altered entries on verification;</LI>
                <LI><Strong>Access control</Strong> — role-based access and least-privilege administration.</LI>
              </UL>
              <P>
                We are pursuing <Strong>SOC 2 (Type I/II)</Strong> and{' '}
                <Strong>ISO 27001 (ISMS)</Strong>; these are on our roadmap and{' '}
                <Strong>are not yet certified</Strong>. More detail is on our{' '}
                <ContactLink to="/security">Security</ContactLink> page. Report a
                suspected vulnerability to{' '}
                <a className="text-accent-700 underline" href="mailto:security@plyez.com">security@plyez.com</a>.
              </P>
            </>
          ),
        },
        {
          id: 'rights',
          title: '9. Your rights and how to exercise them',
          body: (
            <>
              <P>
                Subject to applicable law, you may have the right to:
              </P>
              <UL>
                <LI><Strong>Access</Strong> the personal information we hold about you;</LI>
                <LI><Strong>Correct</Strong> inaccurate or incomplete information;</LI>
                <LI><Strong>Delete</Strong> your account and associated personal data;</LI>
                <LI><Strong>Port</Strong> your data by receiving it in a structured, machine-readable format;</LI>
                <LI><Strong>Object to or restrict</Strong> certain processing;</LI>
                <LI><Strong>Withdraw consent</Strong> (e.g., for marketing) at any time without affecting prior processing.</LI>
              </UL>
              <P>
                To exercise any of these rights, email{' '}
                <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
                (or our Data Protection Officer at{' '}
                <a className="text-accent-700 underline" href="mailto:dpo@plyez.com">dpo@plyez.com</a>)
                from the address on file. We verify identity before acting and respond
                without undue delay, and in any event within the timeframe required by
                applicable law. We do not discriminate against you for exercising your
                rights. If we act as a processor for the data in question, we will
                forward your request to the relevant customer (controller) and assist
                them in responding.
              </P>
            </>
          ),
        },
        {
          id: 'appi',
          title: '10. APPI (Japan) disclosures',
          body: (
            <>
              <P>
                As an entity that handles personal information under the APPI
                (個人情報取扱事業者), the Company observes the following:
              </P>
              <UL>
                <LI>
                  <Strong>Purpose of use (利用目的)</Strong> — we handle personal
                  information within the purposes specified in Section 3 and notify
                  or publish any change in purpose as required.
                </LI>
                <LI>
                  <Strong>Third-party provision (第三者提供)</Strong> — we do not
                  provide personal information to third parties except with your
                  consent, where permitted by law, or as entrustment of handling
                  (委託) to supervised sub-processors.
                </LI>
                <LI>
                  <Strong>Cross-border provision (外国にある第三者への提供)</Strong> —
                  handled as described in Section 6.
                </LI>
                <LI>
                  <Strong>Disclosure and related requests (開示・訂正・利用停止等の請求)</Strong> —
                  you may request disclosure, correction, addition, deletion,
                  cessation of use, or cessation of third-party provision of your
                  retained personal data by contacting{' '}
                  <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>.
                </LI>
              </UL>
              <P>
                If you are not satisfied with our handling of your personal
                information, you may lodge a complaint with the Personal Information
                Protection Commission (個人情報保護委員会, the &ldquo;PPC&rdquo;), the
                supervisory authority for the APPI in Japan.
              </P>
            </>
          ),
        },
        {
          id: 'gdpr',
          title: '11. GDPR (EU/UK) addendum',
          body: (
            <>
              <P>
                Where the GDPR or UK GDPR applies, the following additional terms
                apply to processing for which PlyEZ is the controller. For Customer
                Data we process on a customer&rsquo;s behalf, that customer is the
                controller and our{' '}
                <ContactLink to="/legal/dpa">Data Processing Agreement</ContactLink>{' '}
                governs.
              </P>
              <UL>
                <LI>
                  <Strong>Your rights</Strong> include access, rectification, erasure
                  (&ldquo;right to be forgotten&rdquo;), restriction, data portability,
                  objection (including to processing based on legitimate interests),
                  and the right not to be subject to solely automated decisions with
                  legal or similarly significant effects.
                </LI>
                <LI>
                  <Strong>Legal bases</Strong> are identified in Section 3; transfer
                  safeguards (Standard Contractual Clauses and the UK IDTA) are
                  described in Section 6.
                </LI>
                <LI>
                  <Strong>Data Protection Officer</Strong> — reachable at{' '}
                  <a className="text-accent-700 underline" href="mailto:dpo@plyez.com">dpo@plyez.com</a>.
                </LI>
                <LI>
                  <Strong>EU/UK representative</Strong> — where an Article 27
                  representative is required, the Company has appointed one; contact
                  the DPO above for the current representative&rsquo;s details.
                </LI>
              </UL>
              <P>
                You have the right to lodge a complaint with your local supervisory
                authority (in the EU) or the UK Information Commissioner&rsquo;s Office
                (in the UK).
              </P>
            </>
          ),
        },
        {
          id: 'ccpa',
          title: '12. CCPA/CPRA (California) addendum',
          body: (
            <>
              <P>
                If you are a California resident, the CCPA as amended by the CPRA gives
                you specific rights. In the preceding 12 months we have collected the
                following categories of personal information: identifiers (name, email,
                IP address, OAuth IDs); commercial information (billing and transaction
                records); internet and network activity (usage and log data); geolocation
                (coarse, IP-derived); and professional/employment information (company,
                role). We collect it for the business purposes described in Section 3
                and share it only with the sub-processors in Section 5.
              </P>
              <UL>
                <LI><Strong>Right to know / access</Strong> the categories and specific pieces of personal information we collect;</LI>
                <LI><Strong>Right to delete</Strong> personal information, subject to legal exceptions;</LI>
                <LI><Strong>Right to correct</Strong> inaccurate personal information;</LI>
                <LI><Strong>Right to opt out</Strong> of the sale or sharing of personal information;</LI>
                <LI><Strong>Right to non-discrimination</Strong> for exercising your rights.</LI>
              </UL>
              <P>
                <Strong>We do not sell your personal information, and we do not share
                it for cross-context behavioral advertising.</Strong> Because we do not
                sell or share, there is no &ldquo;Do Not Sell or Share My Personal
                Information&rdquo; action required, but you may still contact us with
                any request. We do not use or disclose sensitive personal information
                for purposes beyond those permitted under the CPRA. To exercise your
                rights, email{' '}
                <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>;
                you may use an authorized agent, and we will verify the request before
                acting.
              </P>
            </>
          ),
        },
        {
          id: 'children',
          title: '13. Children',
          body: (
            <P>
              PlyEZ is a business-to-business service intended for use by companies and
              their authorized personnel. It is not directed to children, and we do not
              knowingly collect personal information from anyone under 16. If you believe
              a minor has provided us personal information, contact{' '}
              <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
              and we will delete it.
            </P>
          ),
        },
        {
          id: 'changes',
          title: '14. Changes to this Policy',
          body: (
            <P>
              We update this Policy from time to time to reflect changes in our
              practices or the law. Material changes will be announced via in-app
              notification and email at least 30 days before they take effect. The
              &ldquo;Last updated&rdquo; date above reflects the current version.
            </P>
          ),
        },
        {
          id: 'contact',
          title: '15. Contact',
          body: (
            <>
              <P>
                For privacy questions or to exercise your rights, contact{' '}
                <a className="text-accent-700 underline" href="mailto:privacy@plyez.com">privacy@plyez.com</a>{' '}
                or our Data Protection Officer at{' '}
                <a className="text-accent-700 underline" href="mailto:dpo@plyez.com">dpo@plyez.com</a>.
                For legal notices, contact{' '}
                <a className="text-accent-700 underline" href="mailto:legal@plyez.com">legal@plyez.com</a>.
              </P>
              <P>
                The data controller is <Strong>[registered company name]</Strong>, a
                Japanese 株式会社 (K.K.), at <Strong>[registered address]</Strong>,
                Japan.
              </P>
            </>
          ),
        },
      ]}
    />
  );
}
